import React, { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { WarningMessage } from '@eika/attention-message';
import { CheckboxPanels } from '@eika/checkbox';
import { CurrencyInput, formatCurrencyValue } from '@eika/currency-input';
import { ExpandableInput } from '@eika/expandable-input';
import { IconTypes } from '@eika/icon';
import { ButtonToggles } from '@eika/radio-button';

import { Laaneformaal, SoeknadLaanContainerWeb, ValgtWeb } from '../../generated-types';
import { useTranslation } from '../../translations';
import { LaanebeloepFormData } from './laanebeloep';
import { maksLaanebeloep, showWarning90Precent } from './laaneberegning';
import Nedbetalingstid from './nedbetalingstid';

interface Props {
    soeknadLaan: SoeknadLaanContainerWeb | undefined;
    featureToggle: boolean | undefined;
}

const FlytteLaan: FC<Props> = ({ soeknadLaan, featureToggle }) => {
    const { boligVerdi, laanebeloep, button, validationMessage } = useTranslation();
    const {
        control,
        setValue,
        watch,
        formState: { errors },
    } = useFormContext<LaanebeloepFormData>();
    const { oekeLaan, soektBeloep, laaneformaal } = watch();

    const onValgtOekeLaan = (value: ValgtWeb) => {
        if (value === ValgtWeb.NEI) {
            setValue('soektBeloep', undefined, { shouldValidate: true });
        }
        setValue('oekeLaan', value, { shouldValidate: true });
    };

    const options: { label: string; value: Laaneformaal }[] = [
        { label: laanebeloep.formaalOppussing, value: Laaneformaal.OPPUSSING },
        { label: laanebeloep.formaalByggeBolig, value: Laaneformaal.BYGGE_PAA_BOLIG },
        { label: laanebeloep.formaalKjoepeBil, value: Laaneformaal.KJOEPE_BIL },
        { label: laanebeloep.formaalInnfriGjeld, value: Laaneformaal.INNFRI_GJELD },
        { label: laanebeloep.formaalAnnet, value: Laaneformaal.ANNET },
    ];

    return (
        <>
            <Controller
                name="oekeLaan"
                control={control}
                rules={{
                    validate: { ikkeValgtOekeLaan: (value: ValgtWeb) => value !== ValgtWeb.IKKE_VALGT },
                }}
                render={({ field: { value } }) => (
                    <ButtonToggles
                        label={boligVerdi.togglesOekeLaan}
                        data-track-id="kreditt-boliglaan-flytte-laan-valg-oeke-laan"
                        data-track-linkname={value}
                        onChange={(value) => onValgtOekeLaan(value as ValgtWeb)}
                        selectedOption={value}
                        options={[
                            { label: button.ja, value: ValgtWeb.JA },
                            { label: button.nei, value: ValgtWeb.NEI },
                        ]}
                        errorMessage={
                            errors.oekeLaan?.type === 'ikkeValgtOekeLaan' ? `${validationMessage.requiredChoose}` : ''
                        }
                    />
                )}
            />
            {oekeLaan === ValgtWeb.JA && (
                <>
                    <Controller
                        name="soektBeloep"
                        control={control}
                        rules={{
                            required: `${validationMessage.requiredValue}`,
                        }}
                        render={({ field: { onChange } }) => (
                            <CurrencyInput
                                name="soektBeloep"
                                label={boligVerdi.labelOekelaan}
                                sublabel={
                                    soeknadLaan?.eiendomVerdi
                                        ? laanebeloep.subLabelOensketLaanebeloep.replace(
                                              '{maksoekning}',
                                              formatCurrencyValue(maksLaanebeloep(soeknadLaan)),
                                          )
                                        : ''
                                }
                                className="max-width-200"
                                placeholder=""
                                type="tel"
                                error={errors.soektBeloep?.message}
                                value={soektBeloep}
                                onValueChange={(v) => onChange(v.value)}
                            />
                        )}
                    />
                    {featureToggle && (
                        <Controller
                            name="laaneformaal"
                            control={control}
                            rules={{
                                required: `${validationMessage.requiredValue}`,
                            }}
                            render={({ field }) => {
                                const fieldValue = field.value || [];
                                return (
                                    <CheckboxPanels
                                        label={laanebeloep.formaal}
                                        error={errors.laaneformaal?.message}
                                        options={options.map((option) => ({
                                            label: option.label,
                                            value: option.value.toString(),
                                            checked: fieldValue.includes(option.value),
                                            onValueChange: (checked) => {
                                                const newValue = checked
                                                    ? [...fieldValue, option.value]
                                                    : fieldValue.filter((val) => val !== option.value);
                                                field.onChange(newValue);
                                            },
                                        }))}
                                    />
                                );
                            }}
                        />
                    )}
                    {laaneformaal?.includes(Laaneformaal.ANNET) && featureToggle && (
                        <Controller
                            name="annetLaaneformaal"
                            control={control}
                            rules={{
                                required: `${validationMessage.requiredValue}`,
                            }}
                            render={({ field }) => (
                                <ExpandableInput
                                    label={laanebeloep.formaalAnnetTitle}
                                    error={errors.annetLaaneformaal?.message}
                                    maxLength={200}
                                    value={field.value}
                                    onChange={field.onChange}
                                    showCharCounter
                                />
                            )}
                        />
                    )}
                    {soeknadLaan?.eiendomVerdi && showWarning90Precent(soeknadLaan, soektBeloep) && (
                        <WarningMessage
                            id="warning85Precent"
                            text={laanebeloep.warningLaaneoekning90}
                            icon={IconTypes.INFO_24}
                        />
                    )}
                </>
            )}
            <Nedbetalingstid />
        </>
    );
};

export default FlytteLaan;
